import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { LabelPage } from "./LabelContent";

/** CSS px per mm (1 CSS px = 1/96 in, also when printing). */
export const PX_PER_MM = 96 / 25.4;

export const LABEL_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans+Condensed:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";

const FONT_WAIT_MS = 3000;

/**
 * Full HTML document with one page per label. Page size equals the label,
 * so the Brother driver prints one die-cut label (or one cut of tape) per page.
 */
export function buildPrintDocument({ elements, records, copies, W, H, title }) {
  const n = Math.max(1, Math.min(99, parseInt(copies, 10) || 1));
  const pages = [];
  records.forEach((record, ri) => {
    for (let c = 0; c < n; c += 1) {
      pages.push(
        <LabelPage key={`${ri}-${c}`} elements={elements} record={record} W={W} H={H} s={PX_PER_MM} />
      );
    }
  });
  const body = renderToStaticMarkup(<>{pages}</>);
  const safeTitle = String(title || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return {
    count: pages.length,
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
<link rel="stylesheet" href="${LABEL_FONTS_URL}">
<style>
@page { size: ${W}mm ${H}mm; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.label-page { page-break-after: always; break-after: page; }
.label-page:last-child { page-break-after: auto; break-after: auto; }
</style></head><body>${body}</body></html>`,
  };
}

function waitForFonts(doc) {
  const ready = doc.fonts?.ready || Promise.resolve();
  return Promise.race([ready, new Promise((r) => setTimeout(r, FONT_WAIT_MS))]);
}

/**
 * Print labels through the browser's print dialog using a hidden iframe.
 * Resolves with the number of labels sent once the dialog has been opened.
 */
export function printLabels(options) {
  const { html, count } = buildPrintDocument(options);
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
    });
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 1000);
    };

    try {
      const doc = iframe.contentDocument;
      doc.open();
      doc.write(html);
      doc.close();
    } catch (err) {
      iframe.remove();
      reject(err);
      return;
    }

    const win = iframe.contentWindow;
    const run = async () => {
      try {
        await waitForFonts(iframe.contentDocument);
        win.addEventListener("afterprint", cleanup, { once: true });
        win.focus();
        win.print();
        // Some browsers never fire afterprint for iframes.
        setTimeout(() => iframe.isConnected && iframe.remove(), 60000);
        resolve(count);
      } catch (err) {
        iframe.remove();
        reject(err);
      }
    };
    if (iframe.contentDocument.readyState === "complete") run();
    else iframe.addEventListener("load", run, { once: true });
  });
}
